/**
 * APSwitch Component
 *
 * A comprehensive switch/toggle component following Figma design system specifications.
 * Supports multiple sizes, states, and accessibility features.
 *
 * @example
 * ```tsx
 * <APSwitch
 *   checked={isEnabled}
 *   onChange={setIsEnabled}
 *   aria-label="Enable notifications"
 * />
 *
 * <APSwitch
 *   size="small"
 *   checked={value}
 *   onChange={(checked) => setValue(checked)}
 *   disabled={isLoading}
 * />
 * ```
 */

import { forwardRef, useId } from 'react';

import type { IAPSwitchProps } from './types';

import './styles.scss';

/**
 * APSwitch - Production-ready switch/toggle component
 *
 * Follows AstroPay design system and accessibility guidelines.
 * Based on Figma Switch component specifications.
 *
 */
export const APSwitch = forwardRef<HTMLInputElement, IAPSwitchProps>(
  (
    {
      checked = false,
      size = 'medium',
      onChange,
      disabled = false,
      className = '',
      'aria-label': ariaLabel,
      id: providedId,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;

    // Handle change event
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event.target.checked, event);
      }
    };

    // Handle keyboard events for Space and Enter keys
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!disabled && (event.key === ' ' || event.key === 'Enter')) {
        event.preventDefault();
        if (onChange) {
          // Create a minimal synthetic event for consistency with the onChange signature
          const syntheticEvent = {
            target: { checked: !checked },
            currentTarget: { checked: !checked },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(!checked, syntheticEvent);
        }
      }
    };

    // Construct class names using BEM methodology
    const baseClasses = 'ap-switch';
    const sizeClasses = `ap-switch--${size}`;
    const stateClasses = [disabled && 'ap-switch--disabled'].filter(Boolean);

    const switchClasses = [baseClasses, sizeClasses, ...stateClasses, className]
      .filter(Boolean)
      .join(' ');

    return (
      <label className={switchClasses} htmlFor={id}>
        <input
          ref={ref}
          id={id}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="ap-switch__input"
          aria-label={ariaLabel}
          role="switch"
          aria-checked={checked}
          {...rest}
        />
        <span className="ap-switch__track" />
        <span className="ap-switch__thumb" />
      </label>
    );
  }
);

APSwitch.displayName = 'APSwitch';

export type { IAPSwitchProps };
export default APSwitch;
